// A share's session view, the pane /s/{id} draws and the exam edition's
// live view of a candidate (RFC-062) (byj-1wv.3.10) — the turn timeline and the
// Screen tab, or a terminal share's terminal; the note and injection inputs;
// a side panel the same for everyone in it (status, alerts, notes or chat,
// people), the notes or chat last, filling what height is left; for the owner
// the policy under the people, and a bar under the head saying how the share
// stands with pause, stop or delete on it (byj-1wv.51.3). Its owner renames
// it from beside its name.
import { Suspense, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { lazyChunk } from "../chunk";
import { api, wsURL, type ShareDetail, type User } from "../api";
import { LiveConnection, type ConnState, type SocketLike } from "../live";
import { capsOf, frame, type Actor, type ControlBody, type Envelope, type Kind, type Policy } from "../protocol";
import { navigate } from "../router";
import { t, word, phrases } from "../i18n";
import {
  applyFrame,
  buildTimeline,
  canInject,
  canNote,
  emptySession,
  namesOf,
  withSentNote,
  type InjectionItem,
  type SessionState,
} from "../timeline";
import { AgentChat, readFrame, type AgentExchange } from "./AgentChat";
import { Chat } from "./Chat";
import { SplitPane } from "./SplitPane";
import { AgentEndpoint } from "./AgentEndpoint";
import { Composer } from "./Composer";
import { useConfirm } from "./Confirm";
import { InvitePopover, PolicyPanel, RequestedTurns, ShareBar } from "./OwnerConsole";
import { useEditTitle } from "./EditTitle";
import { InviteLinksChip, useOpenInvites } from "./OpenInvites";
import { canResume, ResumeHint } from "./ResumeHint";
import { People, PeoplePanel, StatusPanels } from "./SidePanel";
import { panelId, tabId } from "./Tabs";
import { useSplit, ViewBar } from "./ViewBar";
import { Timeline, type Rater, type TimelineHandle } from "./Timeline";
import { AgentUsePanel } from "./AgentUse";
import { useKeptFlag } from "../kept";
import { VSplit } from "./VSplit";

const Screen = lazyChunk(() => import("./Screen"));

type Action =
  | { type: "frame"; e: Envelope }
  | { type: "note"; author: Actor; text: string }
  | { type: "inject"; item: InjectionItem }
  | { type: "reopened" };

type ThreadAction = { type: "ask"; x: AgentExchange } | { type: "frame"; e: Envelope };

/**
 * One person's exchanges with a shared agent (RFC-043 model 20), while they
 * are in flight: the stream frames and results by their ids. An answered
 * exchange leaves the thread — the timeline shows it, and an answer can be
 * long — and a refusal stays, since nothing else shows it, until the next
 * ask.
 */
export function reduceThread(thread: AgentExchange[], a: ThreadAction): AgentExchange[] {
  switch (a.type) {
    case "ask":
      return [...thread.filter((x) => x.error === undefined), a.x];
    case "frame": {
      // a request the relay would not forward comes back as an error naming
      // it: the owner's monthly cap (byj-1wv.46), or a sidecar that is away
      if (a.e.type === "error") {
        const b = a.e.body as { ref?: string; message?: string };
        const i = thread.findIndex((x) => b.ref !== undefined && x.id === b.ref);
        if (i < 0) return thread;
        const out = thread.slice();
        out[i] = { ...thread[i]!, error: b.message ?? "refused" };
        return out;
      }
      if (a.e.type !== "agent.stream" && a.e.type !== "agent.result") return thread;
      const b = a.e.body as { id: string; data?: unknown; response?: unknown };
      const i = thread.findIndex((x) => x.id === b.id);
      if (i < 0) return thread;
      const read = readFrame(a.e.type === "agent.stream" ? b.data : b.response);
      const x = { ...thread[i]! };
      if (read.working !== undefined) x.working = read.working;
      if (read.answer !== undefined) x.answer = read.answer;
      if (read.error !== undefined) x.error = read.error;
      if (a.e.type === "agent.result" && x.answer === undefined && x.error === undefined) x.answer = read.working ?? "";
      if (x.answer !== undefined) return thread.filter((_, j) => j !== i);
      const out = thread.slice();
      out[i] = x;
      return out;
    }
  }
}

function reduce(s: SessionState, a: Action): SessionState {
  switch (a.type) {
    case "frame":
      return applyFrame(s, a.e);
    case "reopened":
      // the share is live again (its owner resumed it): what was said
      // stays, and what comes next follows it
      return { ...s, closed: undefined };
    case "note":
      return withSentNote(s, a.author, a.text);
    case "inject":
      return { ...s, injections: [...s.injections, a.item] };
  }
}

function randomId(): string {
  const b = new Uint8Array(8);
  crypto.getRandomValues(b);
  return "inj_" + Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
}

export function SessionView({
  share,
  me,
  socket,
  onShare,
  recheckMs = 3000,
  resumePollMs = 5000,
  rateApi = api,
}: {
  share: ShareDetail;
  me: User;
  /** The share as the relay says it now, once it is live again: its screen, its start. */
  onShare?: (share: ShareDetail) => void;
  /** A socket factory, for tests. */
  socket?: (url: string) => SocketLike;
  /** How often the owner's page looks again for a recording that is still uploading. */
  recheckMs?: number;
  /** How often an ended share's page asks whether it is live again (byj-1wv.12). */
  resumePollMs?: number;
  /** An agent share's ratings of its answers, for tests (byj-1wv.48). */
  rateApi?: Pick<typeof api, "myRatings" | "rateAnswer">;
}) {
  const [state, dispatch] = useReducer(reduce, emptySession);
  const [thread, dispatchThread] = useReducer(reduceThread, []);
  const [recordings, setRecordings] = useState(share.recordings ?? []);
  const [conn, setConn] = useState<ConnState>({ kind: "connecting" });
  const [policy, setPolicy] = useState<Policy>(share.policy);
  const [tab, setTab] = useState<"timeline" | "screen">(() => firstTab(share));
  // its name, which its owner may change here (byj-1wv.38)
  const [title, setTitle] = useState(share.title);
  const [editTitle, editDialog] = useEditTitle();
  const [confirm, confirmDialog] = useConfirm();
  // what the owner could not do: a rename or a delete the relay refused
  const [actionError, setActionError] = useState<string | null>(null);
  const failed = (err: unknown) => setActionError(err instanceof Error ? err.message : String(err));
  const rename = async () => {
    const next = await editTitle(t("recordings:editShareHeading"), title || "");
    if (next === null) return;
    try {
      setTitle((await api.updateShare(share.id, { title: next })).title);
      setActionError(null);
    } catch (err) {
      failed(err);
    }
  };
  // its owner deletes an ended share, asked first (byj-1wv.34)
  const drop = async () => {
    if (!(await confirm(t("recordings:deleteShareConfirm", { title: title || share.id }), { action: t("recordings:deleteShare"), danger: true }))) return;
    try {
      await api.deleteShare(share.id);
      navigate("/shares");
    } catch (err) {
      failed(err);
    }
  };
  const live = useRef<LiveConnection | null>(null);
  const timeline = useRef<TimelineHandle | null>(null);
  const actor: Actor = useMemo(() => ({ kind: share.role as Kind, user: me.id }), [share.role, me.id]);

  useEffect(() => {
    const c = new LiveConnection({
      url: (since) => wsURL(`/v1/shares/${encodeURIComponent(share.id)}/ws?since=${since}`),
      me: actor,
      share: share.id,
      onFrame: (e) => {
        dispatch({ type: "frame", e });
        dispatchThread({ type: "frame", e });
      },
      onState: setConn,
      onBadFrame: (err) => console.warn("share: refused a frame", err),
      socket,
    });
    live.current = c;
    c.start();
    return () => {
      live.current = null;
      c.stop("left");
    };
  }, [share.id, actor, socket]);

  useEffect(() => {
    if (state.policy) setPolicy(state.policy);
  }, [state.policy]);

  const caps = state.caps ?? capsOf(share.source, share.caps);
  // a terminal share (RFC-043 model 21): the screen is the whole of it, and
  // the notes are its chat; its owner types into it from here
  const terminal = caps.terminal === true;
  // an agent share's card: the page's detail, else the share.open that came over the socket
  const agentInfo = share.agent ?? state.agent;
  const turns = useMemo(() => buildTimeline(state.events), [state.events]);
  const names = useMemo(() => namesOf(share.owner, state.participants, me), [state.participants, share.owner, me]);
  const injects = useMemo(() => state.events.filter((e) => e.body.kind === "inject"), [state.events]);
  // a machine's workbench has had a turn with its agent: its thread is the
  // share's events (RFC-057 D6), a terminal's chat notes aside
  const benchTurns = useMemo(() => caps.workbench === true && state.events.some((e) => e.body.kind === "turn.begin"), [caps.workbench, state.events]);
  // a machine's page is its terminal; the workbench's turns open under it
  // when asked for, as they do in the workbench's agent pane
  const [benchTimeline, setBenchTimeline] = useKeptFlag("share.terminal.timeline", false);

  const sendNote = useCallback(
    (text: string) => {
      const ok = live.current?.send(frame("note", actor, { text }, share.id)) ?? false;
      if (ok) dispatch({ type: "note", author: actor, text });
      return ok;
    },
    [actor, share.id],
  );

  const sendInject = useCallback(
    (text: string) => {
      const id = randomId();
      const ok = live.current?.send(frame("inject.request", actor, { id, text }, share.id)) ?? false;
      if (ok) dispatch({ type: "inject", item: { id, text, by: actor, ts: new Date().toISOString() } });
      return ok;
    },
    [actor, share.id],
  );

  const askAgent = useCallback(
    (skill: string, text: string) => {
      const id = randomId().replace("inj_", "agq_");
      const request = {
        jsonrpc: "2.0",
        id,
        method: "message/stream",
        params: {
          message: {
            messageId: randomId().replace("inj_", "m_"),
            role: "user",
            kind: "message",
            parts: [{ kind: "text", text: `[skill: ${skill}]\n${text}` }],
            contextId: `${share.id}:${me.id}`,
          },
        },
      };
      const ok = live.current?.send(frame("agent.request", actor, { id, request }, share.id)) ?? false;
      if (ok) dispatchThread({ type: "ask", x: { id, skill, text, ts: new Date().toISOString() } });
      return ok;
    },
    [actor, share.id, me.id],
  );

  const sendControl = useCallback(
    (c: ControlBody) => {
      if (!live.current?.send(frame("control", actor, c, share.id))) return;
      if (c.action === "policy" && c.policy) setPolicy(c.policy);
      if (c.action === "pause") setPolicy((p) => ({ ...p, paused: true }));
      if (c.action === "resume") setPolicy((p) => ({ ...p, paused: false }));
    },
    [actor, share.id],
  );

  const onJump = useCallback((seqs: readonly number[]) => {
    setTab("timeline");
    // after the tab switch has rendered the timeline
    setTimeout(() => timeline.current?.scrollToSeqs(seqs), 0);
  }, []);
  const setHandle = useCallback((h: TimelineHandle) => {
    timeline.current = h;
  }, []);

  // removed by the owner: said in a frame before the close, since a browser
  // saw that close as a lost connection and reconnected for good
  // (byj-1wv.10.2) — so the frame, or a 4403, stops the connection here
  const removed = state.removed === true || (conn.kind === "closed" && /removed/.test(conn.reason));
  useEffect(() => {
    if (state.removed) live.current?.stop("removed from this share");
  }, [state.removed]);
  const closed = removed
    ? undefined
    : (state.closed ?? (conn.kind === "closed" && conn.reason !== "left" ? conn.reason : undefined));
  const disabled = conn.kind !== "open" || closed !== undefined || removed;
  // a screen to show: the relay lets it go as the share ends, so an ended
  // share's page is its timeline alone, as it is when opened after the end —
  // not a Screen tab reconnecting for as long as the page is open
  const hasScreen = share.screen === true && closed === undefined && !removed;
  // the screen and the timeline at once, one over the other (components/ViewBar)
  const split = useSplit("share.session.split", hasScreen);
  const splitOn = split.splitOn;

  // an ended share may come back: its owner resumes its session on their
  // machine, and it is shared again under the same id (byj-1wv.12). The
  // page asks for a while, and when it is live again reconnects from its
  // last seq, so what was said stays and what comes next follows it.
  const [resumed, setResumed] = useState(false);
  useEffect(() => {
    if (closed === undefined || removed) return;
    let tries = 0;
    const timer = setInterval(() => {
      tries++;
      api.share(share.id).then(
        (d) => {
          if (!d.live) return;
          clearInterval(timer);
          // what the page was loaded with is the ended share's: no screen,
          // the start before
          onShare?.(d);
          dispatch({ type: "reopened" });
          setResumed(true);
          live.current?.start();
        },
        () => {},
      );
      if (tries >= 720) clearInterval(timer);
    }, resumePollMs);
    return () => clearInterval(timer);
  }, [closed, removed, share.id, resumePollMs, onShare]);
  // the relay says a share has a screen once its first screen has come, which
  // may be moments after it is live again: a session that can carry one is
  // asked about a few times more
  const mayHaveScreen = caps.screen === true;
  useEffect(() => {
    if (!resumed || share.screen || !mayHaveScreen || !onShare) return;
    let tries = 0;
    const timer = setInterval(() => {
      if (++tries >= 12) clearInterval(timer);
      api.share(share.id).then(
        (d) => {
          if (d.live && d.screen) onShare(d);
        },
        () => {},
      );
    }, resumePollMs);
    return () => clearInterval(timer);
  }, [resumed, share.screen, mayHaveScreen, share.id, resumePollMs, onShare]);

  // a recorded share's recording is uploaded as it ends: the owner's page
  // looks again for a while, then links it (byj-1wv.5.8)
  const awaitRecording = share.role === "owner" && closed !== undefined && policy.record === true && recordings.length === 0;
  useEffect(() => {
    if (!awaitRecording) return;
    let tries = 0;
    const timer = setInterval(() => {
      tries++;
      api.share(share.id).then(
        (d) => {
          if (d.recordings?.length) {
            setRecordings(d.recordings);
            clearInterval(timer);
          }
        },
        () => {},
      );
      if (tries >= 10) clearInterval(timer);
    }, recheckMs);
    return () => clearInterval(timer);
  }, [awaitRecording, share.id, recheckMs]);

  const owner = share.role === "owner";
  const ended = closed !== undefined;
  // an agent share: whoever asked rates the answers they were given (byj-1wv.48)
  const [ratings, setRatings] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!caps.agent) return;
    let live = true;
    rateApi.myRatings(share.id).then(
      (r) => live && setRatings(r),
      () => {},
    );
    return () => {
      live = false;
    };
  }, [caps.agent, share.id, rateApi]);
  const rater = useMemo<Rater | undefined>(
    () =>
      caps.agent
        ? {
            mine: (tn) => tn.begin?.body.by?.user === me.id,
            of: (seq) => ratings[String(seq)] ?? 0,
            set: (seq, v) => {
              const was = ratings[String(seq)] ?? 0;
              setRatings((r) => ({ ...r, [String(seq)]: v }));
              rateApi.rateAnswer(share.id, seq, v).catch(() => setRatings((r) => ({ ...r, [String(seq)]: was })));
            },
          }
        : undefined,
    [caps.agent, ratings, me.id, share.id, rateApi],
  );
  const timelineView = (
    <Timeline turns={turns} names={names} handle={setHandle} rater={rater} empty={share.backlog_floor ? t("session:noEventsKept") : undefined} />
  );
  // the owner's hand on the share and its people is there while it is live
  const acting = owner && !ended && !removed;
  // the links that still let someone in, read again as someone new joins,
  // whose link has then been used: not for those there as the page opened,
  // whom its first read already counts, nor as a socket comes back, which
  // uses none (byj-60g)
  const known = useRef<Set<string> | null>(null);
  const [joins, setJoins] = useState(0);
  useEffect(() => {
    const ids = state.participants.map((p) => p.user);
    if (known.current === null) {
      if (ids.length > 0) known.current = new Set(ids);
      return;
    }
    const seen = known.current;
    const fresh = ids.filter((id) => !seen.has(id));
    if (fresh.length === 0) return;
    fresh.forEach((id) => seen.add(id));
    setJoins((n) => n + fresh.length);
  }, [state.participants]);
  // the share's open links, and those made on this page, the relay cannot
  // show again (useOpenInvites keeps both)
  const openInvites = useOpenInvites({ kind: "shares", id: share.id }, acting, String(joins));

  // top to bottom: the session's status and alerts, the people, the owner's
  // policy, and last, filling what is left, the notes (a terminal's chat)
  const side = (
    <div className="side session-side">
      {!terminal && <StatusPanels state={state} onJump={onJump} />}
      {/* the relay counts nobody in an ended share: no one to show, unless
          the page saw them before the end */}
      {(!ended || state.participants.length > 0) && (
        <PeoplePanel
          count={state.participants.length}
          invite={
            acting && (
              <span className="row">
                <InviteLinksChip open={openInvites} />
                <InvitePopover shareId={share.id} caps={caps} made={openInvites.made} onMade={openInvites.add} ownerOnly={policy.owner_only === true} />
              </span>
            )
          }
        >
          <People
            participants={state.participants}
            me={me.id}
            keyboard={state.keyboard}
            onKick={acting ? (user) => sendControl({ action: "kick", user }) : undefined}
            onKeyboard={acting && caps.input ? (user) => sendControl({ action: "keyboard", ...(user ? { user } : {}) }) : undefined}
          />
        </PeoplePanel>
      )}
      {acting && <PolicyPanel source={share.source} caps={caps} policy={policy} onControl={sendControl} />}
      {owner && caps.agent && <AgentUsePanel shareId={share.id} turns={turns.length} />}
      {owner && caps.inject && <RequestedTurns injects={injects} names={names} />}
      <Chat
        kind={terminal ? "chat" : "notes"}
        notes={state.notes}
        names={names}
        me={me.id}
        canSend={canNote(share.role, policy)}
        ownerOnly={policy.notes === false}
        disabled={disabled}
        // a share closed or a person removed is said by the page, never promised a reconnection
        why={closed !== undefined || removed ? undefined : conn.kind === "connecting" ? t("session:connecting") : conn.kind === "waiting" ? t("session:whyOffline") : undefined}
        onSend={sendNote}
        onJump={terminal ? undefined : onJump}
        over={ended}
      />
    </div>
  );

  const back = backOf(share);
  return (
    <div className="session">
      {editDialog}
      {confirmDialog}
      <header className="session-head">
        <a
          className="back"
          href={back.to}
          onClick={(e) => {
            e.preventDefault();
            navigate(back.to);
          }}
          aria-label={back.label}
        >
          ←
        </a>
        {/* its name, and beside it the owner's Edit, which changes only that */}
        <h1>{title || share.id}</h1>
        {owner && (
          // named by what it shows first, so a voice saying "Edit" finds it
          <button className="small" aria-label={t("recordings:editShareName")} onClick={() => void rename()}>
            {t("common:edit")}
          </button>
        )}
        <span className="grow" />
        <span className="muted small">{phrases.source(share.source)}</span>
        <span className={`badge badge-${share.role}`}>
          <span className="badge-role">{word("common:role", share.role)}</span>
        </span>
        {caps.workbench && !ended && !removed && (
          <button className="small timeline-toggle" aria-pressed={benchTimeline} title={t("workbench:timelineTitle")} onClick={() => setBenchTimeline(!benchTimeline)}>
            {t("workbench:timeline")}
          </button>
        )}
        {/* a machine's workbench, for whoever may type into it (RFC-057 D4) */}
        {caps.workbench && !ended && !removed && (owner || (share.role === "coach" && state.keyboard === me.id)) && (
          <button className="small primary" title={t("workbench:openWorkbenchTitle")} onClick={() => navigate(`/s/${encodeURIComponent(share.id)}/workbench`)}>
            {t("workbench:openWorkbench")}
          </button>
        )}
        {ended ? <span className="chip">{t("shares:ended")}</span> : <ConnBadge conn={conn} />}
      </header>
      {owner && !removed && (
        <ShareBar
          ended={ended}
          paused={policy.paused}
          recording={policy.record === true}
          people={state.participants.length}
          startedAt={share.started_at}
          disabled={disabled}
          onControl={sendControl}
          // a class's share stays with the class
          onDelete={ended && !share.class_id ? () => void drop() : undefined}
        />
      )}
      {actionError && <p className="banner error">{t("common:error", { m: actionError })}</p>}
      {removed && <p className="banner">{t("session:removedFromShare")}</p>}
      {closed && <p className="banner">{phrases.shareClosed(closed)}</p>}
      {closed && canResume({ role: share.role, live: false, session: share.session, source: share.source }) && (
        <p className="banner">
          <ResumeHint share={share.id} />
        </p>
      )}
      {!closed && resumed && <p className="banner muted">{t("session:resumed")}</p>}
      {recordings.length > 0 && (
        <p className="banner">
          {t("session:recordedAs")}{" "}
          {recordings.map((r, i) => (
            <span key={r.id}>
              {i > 0 && " · "}
              <a
                href={`/r/${encodeURIComponent(r.id)}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/r/${encodeURIComponent(r.id)}`);
                }}
              >
                {r.uploaded ? phrases.recordingTurns(r.turns[0], r.turns[1]) : t("session:recordingUploading")}
              </a>
            </span>
          ))}
        </p>
      )}
      {closed && awaitRecording && <p className="banner muted">{t("session:recordingAwaited")}</p>}
      {!closed && share.backlog_floor !== undefined && share.backlog_floor > 0 && (
        <p className="banner muted">{t("session:backlogTrimmed", { seq: share.backlog_floor + 1 })}</p>
      )}
      {/* the owner's bar says these to the owner; the banners, to everyone else */}
      {!closed && !owner && policy.paused && <p className="banner">{t("session:paused")}</p>}
      {!closed && !owner && policy.record && <p className="banner">{t("session:recording")}</p>}
      {/* said before a caller asks: the owner keeps what they ask (byj-1wv.48) */}
      {!closed && !owner && caps.agent && policy.keep_questions && <p className="banner" data-testid="keeping-questions">{t("session:keepingQuestions")}</p>}

      {terminal ? (
        <SplitPane
          storageKey="share.terminal.side"
          sideTitle={t("session:chat")}
          main={
            <div className="session-main">
              {ended ? (
                // the relay lets a terminal's screen go as its share ends:
                // nothing to connect to, rather than a connection lost
                <p className="muted screen-ended">{t("session:screenEnded")}</p>
              ) : (
                <VSplit
                  storageKey="share.terminal.thread"
                  label={t("session:splitResize")}
                  topLabel={t("session:tabScreen")}
                  bottomLabel={t("session:tabTimeline")}
                  // a machine's workbench thread is its events (RFC-057 D6):
                  // under the terminal when asked for
                  only={caps.workbench && benchTimeline ? undefined : "top"}
                  top={
                    <Suspense fallback={<p className="muted">{t("common:loading")}</p>}>
                      <Screen
                        shareId={share.id}
                        input={caps.input === true && (owner || (share.role === "coach" && state.keyboard === me.id))}
                        label={owner ? undefined : t("session:screenKeyboardYours")}
                      />
                    </Suspense>
                  }
                  bottom={caps.workbench ? timelineView : null}
                />
              )}
              {ended && benchTurns && timelineView}
            </div>
          }
          side={side}
        />
      ) : (
        <>
          {/* the timeline, and the screen when the session has one: no tab
              bar over a lone view */}
          {hasScreen && (
            <ViewBar prefix="session" label={t("session:sessionTabs")} view={tab} onView={setTab} screenLabel={t("session:tabScreen")} split={split} />
          )}

          <SplitPane
            storageKey="share.session.side"
            sideTitle={t("session:sidePanel")}
            main={
              <div
                className="session-main"
                {...(hasScreen && !splitOn ? { role: "tabpanel", id: panelId("session", tab), "aria-labelledby": tabId("session", tab) } : {})}
              >
                {/* one split for every way of seeing it, as the review page
                    has — the screen over the timeline, or either alone — so
                    the timeline is never drawn again: its folds and where it
                    was scrolled to outlive a tab, the split turned on and
                    off, and the share's end */}
                <VSplit
                  storageKey="share.session.split.top"
                  label={t("session:splitResize")}
                  topLabel={t("session:tabScreen")}
                  bottomLabel={t("session:tabTimeline")}
                  only={splitOn ? undefined : tab === "screen" && hasScreen ? "top" : "bottom"}
                  top={
                    <Suspense fallback={<p className="muted">{t("common:loading")}</p>}>
                      <Screen shareId={share.id} />
                    </Suspense>
                  }
                  bottom={timelineView}
                />
                {/* nothing to write into once the share is over */}
                {!ended &&
                  (agentInfo && canAsk(share.role) ? (
                    <AgentChat agent={agentInfo} thread={thread} disabled={disabled} onAsk={askAgent} />
                  ) : (
                    canInject(share.role, caps, policy) && (
                      <Composer injections={state.injections} disabled={disabled} onInject={sendInject} />
                    )
                  ))}
                {agentInfo && canAsk(share.role) && !ended && <AgentEndpoint shareId={share.id} />}
              </div>
            }
            side={side}
          />
        </>
      )}
    </div>
  );
}

/**
 * The tab a share's page opens on (byj-1wv.51.3): its screen whenever it has
 * one, whatever its kind — what is happening, as it happens — and its
 * timeline when it has none, an ended share's included.
 */
export function firstTab(share: Pick<ShareDetail, "screen">): "timeline" | "screen" {
  return share.screen ? "screen" : "timeline";
}

/**
 * Where the page's ← goes: a class's share, to its instructor — come from
 * the dashboard — goes back to the class's dashboard; to anyone else, the
 * student who shared it and the people they invited included, the Shares
 * page, which lists it (a class they are not in would not open).
 */
function backOf(share: Pick<ShareDetail, "class_id" | "instructor">): { to: string; label: string } {
  if (share.class_id && share.instructor) return { to: `/c/${encodeURIComponent(share.class_id)}`, label: t("classes:backToDashboard") };
  return { to: "/shares", label: t("common:backToShares") };
}

/** Who asks a shared agent: callers, the owner and coaches (RFC-043 model 20). */
export function canAsk(role: string): boolean {
  return role === "caller" || role === "owner" || role === "coach";
}

function ConnBadge({ conn }: { conn: ConnState }) {
  switch (conn.kind) {
    case "open":
      return <span className="chip chip-live">{t("session:connected")}</span>;
    case "connecting":
      return <span className="chip">{t("session:connecting")}</span>;
    case "waiting":
      return <span className="chip chip-warn">{t("session:reconnecting", { s: Math.ceil(conn.retryInMs / 1000) })}</span>;
    case "closed":
      return null;
  }
}
